import { getOperatorArtifactSchema } from '@kanbu/schema/contracts';
import { ThreadMode } from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import { OperatorInfo } from './components/operator-info';
import { Button } from '../../ui';
import { ToolError, ToolStatus } from '../components';
import { createToolComponent } from '../tool-registry';

export interface GetOperatorToolProps {
  onTakeover?: (accepted: boolean) => Promise<void>;
  mode?: ThreadMode;
}

/**
 * Handoff UI only — no boilerplate copy. The model's answer text sits above
 * (partition keeps operator cards after text) and covers availability wording.
 * Contact card always; connect button only while a human is available.
 */
export const GetOperatorTool = createToolComponent<
  GetOperatorToolProps,
  never,
  never,
  typeof getOperatorArtifactSchema
>(
  {
    schema: {
      artifact: getOperatorArtifactSchema,
    },
    validate: false,
    position: 'after',
  },
  ({ state, error, artifact, mode, onTakeover }) => {
    const { operator, available = true } = artifact ?? {};
    const [isLoading, setIsLoading] = useState(false);

    const handleTakeover = async (accepted: boolean) => {
      try {
        if (!onTakeover) {
          return;
        }

        setIsLoading(true);
        await onTakeover(accepted);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    switch (state) {
      case 'call':
        return (
          <ToolStatus loading>
            <Trans>Searching...</Trans>
          </ToolStatus>
        );

      case 'call-partial':
      case 'call-complete':
        return (
          <ToolStatus loading>
            <Trans>Looking for operators...</Trans>
          </ToolStatus>
        );

      case 'error':
        return (
          <ToolError message={<Trans>Unknown error</Trans>} error={error} />
        );

      case 'result': {
        const showOperatorContact = operator?.firstName && operator?.lastName;
        const showConnect = available && Boolean(onTakeover);

        return (
          <div className='flex w-full flex-col items-start gap-2'>
            {showOperatorContact ? (
              <OperatorInfo
                operator={{
                  name: formatUtils.fullName(operator),
                  email: operator.email,
                  phone: operator.phone,
                }}
              />
            ) : null}
            {showConnect ? (
              <Button
                loading={isLoading}
                disabled={isLoading}
                variant={mode === ThreadMode.Human ? 'outline' : 'primary'}
                onClick={() => handleTakeover(mode === ThreadMode.AI)}
                className='!rounded-full'
              >
                {mode === ThreadMode.Human ? (
                  <Trans>Go back to AI</Trans>
                ) : (
                  <Trans>Yes, connect me</Trans>
                )}
              </Button>
            ) : null}
          </div>
        );
      }

      default:
        return null;
    }
  },
);
